/**
 * Pure playback-correction math for the client sync engine (WS-8).
 *
 * No DOM, no I/O, no side effects — fully unit-testable. Driven by
 * `useRoomSync` on every `sync:apply` event: it projects the authoritative
 * server state to "now", compares against the player's `currentTime`, and
 * asks for one of three corrections. All thresholds come from the frozen
 * `SYNC` constant in @syncreel/shared — no magic numbers here.
 *
 * @see @syncreel/shared time.ts projectPosition — produces the `expected` input.
 * @see Jellyfin SyncPlay — two-tier (soft rate nudge / hard seek) pattern.
 */
import { SYNC } from '@syncreel/shared';

/** Result of a correction decision. `useRoomSync` applies it to the player. */
export type Correction =
  /** Drift inside the ignore band — do nothing (avoid jitter from noise). */
  | { kind: 'none' }
  /** Small drift — nudge playbackRate for a bounded window, then revert. */
  | { kind: 'soft'; rate: number; windowMs: number }
  /** Large drift — hard seek via currentTime() to the authoritative position. */
  | { kind: 'hard'; position: number };

/** Inputs to {@link computeCorrection}. */
export interface CorrectionInput {
  /** Authoritative projected position (seconds) at the comparison instant. */
  expected: number;
  /** Player's current position (seconds) at the comparison instant. */
  actual: number;
  /** Base playback rate from the authoritative state (e.g. 1, 1.25). */
  baseRate: number;
  /** Whether the authoritative state says playback should be advancing. */
  isPlaying: boolean;
}

/**
 * Decide how to correct a player position toward the authoritative one.
 *
 * Rules (per §WS-8):
 *   |drift| <  IGNORE_BAND_S   -> none
 *   |drift| <  SOFT_SYNC_MAX_S -> soft, rate = baseRate * (1 ± SOFT_RATE_DELTA)
 *   otherwise                  -> hard seek to `expected`
 *
 * When the player is paused (`isPlaying === false`) a soft rate nudge is
 * useless — playbackRate has no effect while paused — so any drift above the
 * ignore band falls through to a hard seek. This is the safer option (§11.8):
 * a paused viewer drifting inside the soft band would otherwise never
 * converge.
 *
 * @see SYNC in @syncreel/shared for the threshold values.
 */
export function computeCorrection(args: CorrectionInput): Correction {
  const drift = args.expected - args.actual;
  const absDrift = Math.abs(drift);

  if (absDrift < SYNC.IGNORE_BAND_S) {
    return { kind: 'none' };
  }

  if (absDrift < SYNC.SOFT_SYNC_MAX_S) {
    // WHY: a rate nudge only moves the needle while the media is playing; when
    // paused the only way to converge is a hard seek, so skip soft sync.
    if (!args.isPlaying) {
      return { kind: 'hard', position: args.expected };
    }
    // Behind the authority -> speed up; ahead -> slow down.
    const sign = drift > 0 ? 1 : -1;
    const rate = args.baseRate * (1 + sign * SYNC.SOFT_RATE_DELTA);
    return { kind: 'soft', rate, windowMs: SYNC.SOFT_WINDOW_MS };
  }

  return { kind: 'hard', position: args.expected };
}
